import { CommerceService } from "@/services/commerce"

const commerceApi = CommerceService.api()

/**
 * Retrieves a customer based on the provided parameters.
 *
 * @param {Object} options - The options for retrieving the customer.
 * @param {string} options.id - The ID of the customer.
 * @param {string} options.key - The key of the customer.
 * @param {string} options.number - The number of the customer.
 * @returns {Promise<Object|null>} - A promise that resolves to the retrieved customer object, or null if no customer is found.
 */
export const getCustomer = async ({ id, key, number }) => {
  let customer = {}

  if (key) {
    customer = await getCustomerByKey(key)
  } else if (id) {
    customer = await getCustomerById(id)
  } else if (number) {
    customer = await getCustomerByNumber(number)
  }

  return customer ?? null
}

const getCustomerByKey = async (key) => {
  return (
    await commerceApi
      .customers()
      .withKey({ key })
      .get({ queryArgs: { expand: ["customerGroup"] } })
      .execute()
  )?.body
}

const getCustomerById = async (id) => {
  return (
    await commerceApi
      .customers()
      .withId({ ID: id })
      .get({ queryArgs: { expand: ["customerGroup", "custom.fields.discountKey"] } })
      .execute()
  )?.body
}

/**
 * Returns a customer based on customer number
 *
 * @param {string} customerNumber
 *
 * @returns {Promise<customer>|null}
 */
export const getCustomerByNumber = async (customerNumber) => {
  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          expand: ["customerGroup", "custom.fields.industryKey", "custom.fields.discountKey"],
          where: `customerNumber="${customerNumber}"`,
        },
      })
      .execute()
  )?.body?.results

  const customerObject = getCommonCustomerObjectInformationFromArray(customers)

  if (!customerObject) return null

  const { customer, defaultBillingAddress } = customerObject

  const { custom: customFields } = customer

  return {
    ...customer,
    customFields: {
      customerBranchId: customFields?.fields?.deliveryBranch ?? customFields?.fields?.customerBranch,
      customerClassification: customFields?.fields?.customerClassification ?? null,
      tourNumber: customFields?.fields?.tourNumber ?? null,
      admSalesRepresentive: customFields?.fields?.admSalesRepresentive ?? null,
      paymentMethod: customFields?.fields?.paymentMethod ?? null,
      einzaCreditLimit: customFields?.fields?.einzaCreditLimit ?? null,
    },
    defaultBillingAddress,
  }
}

/**
 * Returns an adm based on customer number
 *
 * @param {string} admNumber
 *
 * @returns {Promise<customer>|null}
 */
export const getAdmByNumber = async (admNumber) => {
  const adm = (
    await commerceApi
      .customers()
      .get({ queryArgs: { where: `custom(fields(admNumber="${admNumber}"))` } })
      .execute()
  )?.body?.results

  const customerObject = getCommonCustomerObjectInformationFromArray(adm)

  if (!customerObject) return null

  const { customer: admUser, defaultBillingAddress } = customerObject

  const { custom: customFields } = admUser

  return {
    ...admUser,
    customAdmFields: {
      admNumber: customFields?.fields?.admNumber ?? null,
    },
    defaultBillingAddress,
  }
}

/**
 * Takes array of customers and returns an object
 *
 * @param {array} customers
 *
 * @returns {customer: object, defaultBillingAddress: object}
 */
const getCommonCustomerObjectInformationFromArray = (customers) => {
  if (customers?.length <= 0) return null

  const customer = customers[0]

  const { defaultBillingAddressId, addresses } = customer

  return {
    customer,
    defaultBillingAddress: addresses.find((address) => address?.id === defaultBillingAddressId) ?? null,
  }
}

/**
 * Gets multiple customers by provided customer numbers from commercetools
 *
 * @param {string[]} customerNumbers
 *
 * @returns {Promise<array>}
 */
export const getMultipleCustomersByNumbers = async (customerNumbers) => {
  if (!customerNumbers || customerNumbers.length === 0) return []
  const customerNumbersAsStrings = customerNumbers.map((el) => `"${el}" `)

  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: { where: `customerNumber in (${customerNumbersAsStrings})`, limit: 500 },
      })
      .execute()
  )?.body?.results

  if (!customers || customers.length === 0) return []

  return customers
}
